import '../../styles/restScreens.css';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { featureFlags } from '../../config/featureFlags';

const STATUSES = [
    { value: 'not_started', label: 'Not started' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'completed', label: 'Complete' },
];

function shortId(id) {
    return id ? `Staff ${id.slice(0, 8)}` : 'Staff member';
}

function ClientOnboardingChecklist({ account, readOnly = false, welcome = false }) {
    const [steps, setSteps] = useState([]);
    const [staffNames, setStaffNames] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [auditError, setAuditError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [savingStep, setSavingStep] = useState('');

    const loadSteps = useCallback(async (isActive = () => true) => {
        setLoading(true);
        setLoadError('');
        setAuditError('');
        const { data, error } = await supabaseClient
            .from('client_onboarding_steps')
            .select('org_id, account_id, step_definition_id, status, completed_at, created_at, created_by, updated_at, updated_by, step_definition:onboarding_step_definitions(key, label, sort_order)')
            .eq('org_id', account.org_id)
            .eq('account_id', account.id)
            .order('created_at', { ascending: true });
        if (!isActive()) return;
        if (error) {
            setLoadError(welcome ? 'Your account setup checklist could not be loaded. Please try again.' : error.message || 'The onboarding checklist could not be loaded.');
            setSteps([]);
            setLoading(false);
            return;
        }

        const rows = [...(data || [])].sort((left, right) => {
            const leftOrder = Number.isInteger(left.step_definition?.sort_order)
                ? left.step_definition.sort_order
                : Number.MAX_SAFE_INTEGER;
            const rightOrder = Number.isInteger(right.step_definition?.sort_order)
                ? right.step_definition.sort_order
                : Number.MAX_SAFE_INTEGER;
            return leftOrder - rightOrder
                || (left.step_definition?.key || '').localeCompare(right.step_definition?.key || '');
        });
        const actorIds = [...new Set(rows
            .flatMap((row) => [row.updated_by, row.created_by])
            .filter(Boolean))];
        let names = {};
        if (actorIds.length && !welcome) {
            const { data: profiles, error: profileError } = await supabaseClient
                .from('user_profiles')
                .select('id, full_name')
                .in('id', actorIds);
            if (!isActive()) return;
            if (profileError) {
                console.error('Unable to load client onboarding audit names:', profileError);
                setAuditError('Staff names are unavailable; the checklist audit IDs are shown instead.');
            } else {
                names = Object.fromEntries((profiles || []).map((profile) => [
                    profile.id,
                    profile.full_name?.trim() || shortId(profile.id),
                ]));
            }
        }
        setStaffNames(names);
        setSteps(rows);
        setLoading(false);
    }, [account.id, account.org_id, welcome]);

    useEffect(() => {
        let active = true;
        loadSteps(() => active);
        return () => { active = false; };
    }, [loadSteps]);

    const completedCount = useMemo(
        () => steps.filter((step) => step.status === 'completed').length,
        [steps]
    );
    const progress = steps.length ? Math.round((completedCount / steps.length) * 100) : 0;

    const updateStatus = async (step, status) => {
        if (readOnly || savingStep) return;
        setSavingStep(step.step_definition_id);
        setSaveError('');
        const { data, error } = await supabaseClient
            .from('client_onboarding_steps')
            .update({ status })
            .eq('org_id', account.org_id)
            .eq('account_id', account.id)
            .eq('step_definition_id', step.step_definition_id)
            .select('step_definition_id')
            .maybeSingle();
        if (error) {
            setSaveError(error.message || 'The checklist status could not be saved.');
            setSavingStep('');
            return;
        }
        if (!data) {
            setSaveError('No checklist row was updated. Confirm you still have access to this client account.');
            setSavingStep('');
            return;
        }
        await loadSteps();
        setSavingStep('');
    };

    return (
        <section data-m6-layout="clients" data-m6-component="ClientOnboardingChecklist" className="ia-hover-pulse-scope space-y-4" aria-label="Client onboarding checklist">
            {welcome && (
                <header className="space-y-1">
                    <h2 className="text-xl font-semibold text-[var(--ink)]">
                        Welcome{account.account_name ? `, ${account.account_name}` : ''}
                    </h2>
                    <p className="text-sm text-[var(--ink-muted)]">
                        Your onboarding checklist and progress are shown here.
                    </p>
                </header>
            )}

            {!welcome && featureFlags.bl362ClientOnboardingPreview && (
                <div className="flex justify-end">
                    <a
                        className="ia-hover-pulse rounded-full border border-[var(--surface-border)] px-3 py-1.5 text-xs text-[var(--ink-muted)]"
                        href={`/client-onboarding-preview/${account.id}`}
                    >
                        Preview client welcome
                    </a>
                </div>
            )}

            {loading && <p className="text-sm text-[var(--ink-muted)]">Loading onboarding checklist…</p>}
            {loadError && <p className="text-sm text-[var(--ui-alert)]" role="alert">{loadError}</p>}
            {auditError && <p className="text-xs text-[var(--ui-gold)]" role="status">{auditError}</p>}
            {saveError && <p className="text-sm text-[var(--ui-alert)]" role="alert">{saveError}</p>}

            {!loading && !loadError && (
                steps.length === 0 ? (
                    <p className="rounded-lg border border-[var(--surface-border)] p-4 text-sm text-[var(--ink-muted)]">
                        {welcome ? 'Your account team will add your setup steps here.' : 'No client onboarding steps are configured yet. The client catalog stays empty until its steps are approved.'}
                    </p>
                ) : (
                    <>
                        <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm text-[var(--ink)]">
                                <span>Progress</span>
                                <span>{completedCount} of {steps.length} complete · {progress}%</span>
                            </div>
                            <div
                                className="h-2 overflow-hidden rounded-full bg-[var(--surface-sunken)]"
                                role="progressbar"
                                aria-label="Onboarding progress"
                                aria-valuemin="0"
                                aria-valuemax={steps.length}
                                aria-valuenow={completedCount}
                            >
                                <div
                                    className="h-full rounded-full bg-[var(--selected-pill-border)] transition-all"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </div>

                        <ol className="divide-y divide-[var(--surface-border)] rounded-lg border border-[var(--surface-border)]">
                            {steps.map((step) => {
                                const actorId = step.updated_by || step.created_by;
                                const actor = staffNames[actorId] || shortId(actorId);
                                return (
                                    <li
                                        key={`${step.account_id}:${step.step_definition_id}`}
                                        className="space-y-3 p-4"
                                    >
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <h3 className="text-sm font-medium text-[var(--ink)]">
                                                {step.step_definition?.label || 'Onboarding step'}
                                            </h3>
                                            <span className="text-xs text-[var(--ink-muted)]">
                                                {STATUSES.find((status) => status.value === step.status)?.label || step.status}
                                            </span>
                                        </div>
                                        {!readOnly && (
                                            <>
                                                <div className="flex flex-wrap gap-2" role="group" aria-label={`Status for ${step.step_definition?.label || 'onboarding step'}`}>
                                                    {STATUSES.map((status) => {
                                                        const selected = step.status === status.value;
                                                        return (
                                                            <button
                                                                key={status.value}
                                                                type="button"
                                                                disabled={Boolean(savingStep)}
                                                                aria-pressed={selected}
                                                                onClick={() => updateStatus(step, status.value)}
                                                                className={`ia-hover-pulse rounded-full border bg-transparent px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
                                                                    selected
                                                                        ? 'ia-selected-pill border-[var(--selected-pill-border)] text-[var(--selected-pill-ink)]'
                                                                        : 'border-[var(--ui-border)] text-[var(--ink-muted)]'
                                                                }`}
                                                            >
                                                                {status.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                                {savingStep === step.step_definition_id && (
                                                    <p className="text-xs text-[var(--ink-muted)]" role="status">Saving status…</p>
                                                )}
                                            </>
                                        )}
                                        <p className="text-xs text-[var(--ink-muted)]">
                                            {actorId
                                                ? `Last updated by ${welcome ? 'your account team' : actor} · ${new Date(step.updated_at || step.created_at).toLocaleString()}`
                                                : `Added · ${new Date(step.created_at).toLocaleString()}`}
                                        </p>
                                        {step.completed_at && (
                                            <p className="text-xs text-[var(--ink-muted)]">
                                                Completed {new Date(step.completed_at).toLocaleString()}
                                            </p>
                                        )}
                                    </li>
                                );
                            })}
                        </ol>
                    </>
                )
            )}
        </section>
    );
}

export default ClientOnboardingChecklist;
